/// <reference types="@types/googlemaps" />
import {ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {v4 as UUID} from 'uuid';
import {MathUtils} from '../../commons/math-utils';
import {DeviceService} from '../../commons/device.service';
import {DirectionsStateService} from '../../direction/directions.state.service';
import {MonitorMarkerService} from '../../map/monitor-marker.service';
import {MapSettings} from '../../model/map.constants';
import {AuthStorage} from '../../auth/auth-storage';
import {PlacesService, POST_CODE_REGEX} from '../places/places.service';
import {PlaceSource} from '../places/model/place-source';
import {DomSanitizer} from '@angular/platform-browser';
import {TaskUtils} from '../../../pages/client/tasks/tasks-manager/utils/task-utils';
import {TasksManagerState} from '../../../pages/client/tasks/tasks-manager/tasks-manager-state.service';
import {Alphabet} from '../../model/alphabet';
import {NotificationService} from '../../notification/notification.service';
import {ClipboardService} from 'ngx-clipboard';
import {
  ContactlessType,
  ProTasksFormStateService
} from '../../../pages/client/tasks/pro-tasks-manager/form/pro-tasks-form-state.service';
import {Subject, Subscription, takeUntil, distinctUntilChanged, debounceTime} from 'rxjs';
import {SubscriptionUtils} from '../../commons/subscription.utils';
import * as InfoBubble from '../../../../js/infobubble.js';
import {Module} from '../../model/profile';

export const ZOOM = {
  DEFAULT: 8,
  DELTA: 4,
  MIN: 3
};

export interface AddressData {
  uuid: string;
  location: google.maps.LatLng;
  selection: string;
  address: string;
}

@Component({
  selector: 'app-map-address-search',
  templateUrl: './map-address-search.component.html',
  styleUrls: ['./map-address-search.component.scss']
})
export class MapAddressSearchComponent implements OnInit, OnDestroy {

  @Input()
  public map: google.maps.Map;

  @Input()
  public tasksCount = 0;

  @Input()
  public proTasksForm = false;

  @Input()
  public proTasks = false;

  @Input()
  public disableRightClick = false;

  private datasMap: Map<string, AddressData> = new Map<string, AddressData>();

  private markersMap: Map<string, any> = new Map<string, any>();
  private infosMap: Map<string, any> = new Map<string, any>();

  public planner: boolean;
  public recenterLastMarker: boolean;
  public gSearch: string;
  public searchResults: any[] = [];
  public lastMarkerPosition: string;
  private rightClickListener: any;
  private markerSubscription: Subscription;
  private subSubject = new Subject<boolean>();
  private dropDownIndex = -1;
  private searchSubject = new Subject<string>();

  private COORDINATES = '([+-]?\\d+\\.?\\d+)\\s*,\\s*([+-]?\\d+\\.?\\d+)';

  constructor(private markerService: MonitorMarkerService,
              private deviceService: DeviceService,
              private translate: TranslateService,
              private directionsState: DirectionsStateService,
              private ngZone: NgZone,
              private placesService: PlacesService,
              private sanitizer: DomSanitizer,
              private cdRef: ChangeDetectorRef,
              private taskService: TasksManagerState,
              private notification: NotificationService,
              private clipBoardService: ClipboardService,
              private proTaskStateService: ProTasksFormStateService) {
  }

  public ngOnInit(): void {
    window['angularAddrSearchRef'] = {component: this, zone: this.ngZone};
    let addressSearch = document.getElementById('address_search');

    let controls = this.map.controls[google.maps.ControlPosition.LEFT_TOP];
    addressSearch['index'] = MapSettings.MAP_ADDRESS_SEARCH_INDEX;
    controls.push(addressSearch);
    this.markerSubscription = this.placesService.getMarker()
      .pipe(takeUntil(this.subSubject))
      .subscribe(data => this.showMarker(data));
    this.searchSubject.pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.subSubject))
      .subscribe(event => this.handleDebounce(event));
    this.handlePasteEvent();
    this.addMouseEvents();
  }

  private removeMarkers() {
    this.markersMap.forEach(it => it.setMap(null));
    this.infosMap.forEach(it => it.setMap(null));
    this.markersMap.clear();
    this.infosMap.clear();
  }

  public setupMarker(data: AddressData): void {
    if (data.location.lat() && data.location.lng()) {
      this.datasMap.set(data.uuid, data);
      this.lastMarkerPosition = data.uuid;
      this.recenterLastMarker = true;
      let infoWindow = this.createInfoWindow(data);
      this.infosMap.set(data.uuid, infoWindow);
      let marker = this.createMarker(data, infoWindow);
      this.markersMap.set(data.uuid, marker);
      let zoom = this.map.getZoom() > ZOOM.DEFAULT ? this.map.getZoom() : ZOOM.DEFAULT;
      infoWindow.open(this.map, marker);
      this.map.setCenter(data.location);
      this.map.setZoom(zoom);
    }
  }

  public hideMarker(uuid: string) {
    this.infosMap.get(uuid).close();
    this.markersMap.get(uuid).setVisible(false);
  }

  public zoomMarker(uuid: string, delta: number) {
    this.map.setCenter(this.datasMap.get(uuid).location);
    this.map.setZoom(Math.max(ZOOM.MIN, this.map.getZoom() + delta));
  }

  public addToRoute(uuid: string) {
    this.enablePlanner();
    this.directionsState.directionPoint({
      marker: this.markersMap.get(uuid),
      info: this.infosMap.get(uuid)
    });
  }

  public findNearest(location: string) {
    this.directionsState.nearestObject(location);
  }

  private createInfoWindow(data: AddressData): any {
    let latitude = MathUtils.roundCoordinates(data.location.lat());
    let longitude = MathUtils.roundCoordinates(data.location.lng());

    let coordinates = '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span>: ${latitude}, ${longitude}`
      + `<a class="map-popup__link" `
      + `title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '" `
      + `onclick="window.angularAddrSearchRef.zone.run(() => `
      + '{window.angularAddrSearchRef.component.' + `copyToClipboard('${latitude},${longitude}')` + '})">'
      + '<i class="ico-copy"></i></a></div>';

    let address = '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${data.address}</div>`;

    let zoomPlusLink = MapAddressSearchComponent
      .getActionLink(`zoomMarker('${data.uuid}', ${ZOOM.DELTA})`, 'ico-zoom-in', 'zoomIn', this.translate);
    let zoomMinusLink = MapAddressSearchComponent
      .getActionLink(`zoomMarker('${data.uuid}', ${-ZOOM.DELTA})`, 'ico-zoom-out', 'zoomOut', this.translate);
    let hideLink = MapAddressSearchComponent
      .getActionLink(`hideMarker('${data.uuid}')`, 'ico-close', 'close', this.translate);
    let nearest = MapAddressSearchComponent
      .getActionLink(`findNearest('${data.location}')`, 'ico-target', 'map', this.translate);

    let content = '<div class="popup-content">';
    content += `<div class="popup-header">${data.selection === null ? '' : data.selection}</div>`;
    content += `<div class="ol-info-data">${coordinates}</div>`;
    content += `<div class="ol-info-data">${address}</div>`;
    content += `<div class="popup-buttons">${hideLink}${zoomPlusLink}${zoomMinusLink}`;
    if (this.isClientEmployee() && !this.proTasks) {
      content += `${nearest}`;
    }
    if (!this.proTasks && this.canManageTasks()) {
      for (let i = 0; i <= this.tasksCount; i++) {
        let insertTask = '<a class="icon-action" '
          + `title="${this.translate.instant('monitor.map.title.insertInto')}"`
          + `onclick="window.angularAddrSearchRef.zone.run(() => `
          + `{window.angularAddrSearchRef.component.insertInto(`
          + `${data.location.lat()}, ${data.location.lng()}, ${i}, '${data.uuid}', null)})">`
          + `${i > 0 ? Alphabet.ARRAY[i - 1] + ' ' : '<i class="ico-car"> </i> '}`
          + '<i class="ico-arrow-right"></i>'
          + `  ${Alphabet.ARRAY[i]}`
          + '</a>';
        content += `${insertTask}`;
      }
    }
    if (this.proTasksForm && this.canManageTasks()) {
      content += MapAddressSearchComponent
        .taskInsertActionLink(latitude, longitude, this.translate, 'autoImport', false);
      content += MapAddressSearchComponent
        .taskInsertActionLink(latitude, longitude, this.translate, 'importToLastPosition', true);
    }
    content += '</div></div>';

    let isSafari = this.deviceService.isSafariBrowser();

    return new InfoBubble({
      content: content,
      shadowStyle: 1,
      closeSrc: 'assets/img/app/fa-close-dark.svg',
      borderWidth: 0,
      arrowSize: 5,
      minHeight: isSafari ? 130 : 110,
      maxHeight: isSafari ? 177 : 172,
      minWidth: 265,
      maxWidth: 300
    });
  }

  private createMarker(data: AddressData, infoWindow: any): any {
    let marker = new MarkerWithLabel({
      map: this.map,
      position: data.location,
      icon: 'assets/img/marker/addr_search.svg',
      label: data.selection,
      labelVisible: true
    });

    let map = this.map;
    marker.addListener('click', function () {
      infoWindow.open(map, marker);
    });

    return marker;
  }

  private static getActionLink(action: string, icon: string, tooltip: string, translate: TranslateService): string {
    return '<a class="icon-action" '
      + 'title ="' + translate.instant('monitor.map.title.' + tooltip) + '"'
      + 'onclick="window.angularAddrSearchRef.zone.run(() => '
      + '{window.angularAddrSearchRef.component.' + action + '})"><i class="' + icon + '"></i></a>';
  }

  private static taskInsertActionLink(
    lat: number,
    lng: number,
    translate: TranslateService,
    tooltip: string,
    autoInsert: boolean) {

    return '<a class="icon-action" '
      + `onclick="window.angularPoiRef.zone.run(() => `
      + `{window.angularAddrSearchRef.component.insertProTask(${lat}, ${lng}, ${autoInsert})})">`
      + `${translate.instant('proTasks.map.' + tooltip)}`
      + '</a>';
  }

  public insertProTask(lat: number,
                       lng: number,
                       lastPosition: boolean,
                       externalId: any,
                       poiId: number,
                       contactlessType?: ContactlessType) {

    this.proTaskStateService.triggerInsertTaskFromPOI({
      lat: lat,
      lng: lng,
      lastPosition: lastPosition,
      externalId: externalId,
      poiId: poiId,
      contactlessType: contactlessType
    });
  }

  public enablePlanner() {
    if (this.isClientEmployee()) {
      this.directionsState.direction({});
    }
  }

  private addMouseEvents() {
    if (!this.disableRightClick) {
      let self = this;
      this.rightClickListener =
        google.maps.event.addListener(this.map, 'rightclick', function (event) {
          let sources = [PlaceSource.NOMINATIM_COORDINATES];
          let coordinates = `${event.latLng.lat().toFixed(6)},${event.latLng.lng().toFixed(6)}`;
          self.placesService.getPlaces(coordinates, sources)
            .pipe(takeUntil(self.subSubject))
            .subscribe(
              (data) => {
                let addressData = self.getData(event.latLng.lat(), event.latLng.lng());
                addressData.address = data.length > 1 ? data[1].name : '';
                self.setupMarker(addressData);
              }
            );
        });
    }
  }

  private removeMouseEvents() {
    google.maps.event.removeListener(this.rightClickListener);
  }

  private getData(lat: number, lng: number): AddressData {

    return {
      uuid: UUID(),
      location: new google.maps.LatLng(lat, lng),
      selection: null,
      address: ''
    };
  }

  public isClientEmployee(): boolean {
    return AuthStorage.isLoggedClientEmployee();
  }

  public canManageTasks(): boolean {
    return AuthStorage.isLoggedClientEmployee() || AuthStorage.getModules().includes(Module.MANAGEMENT);
  }

  public showMore() {
    if (this.gSearch) {
      let sources = [PlaceSource.GOOGLE_ADDRESS];
      this.placesService.getPlaces(this.gSearch, sources)
        .pipe(takeUntil(this.subSubject))
        .subscribe(
          data => {
            this.searchResults = this.searchResults.concat(data);
            this.hideShowMore();
            this.cdRef.markForCheck();
          });
    }
  }

  public showResultOnMap(result: any) {
    window['angularAddrSearchRef'].zone.run(() => {
      if (result.lat) {
        this.showMarkerOnMap(result.lat, result.lng, result.name);
      } else {
        this.placesService.placeDetails(result.placeId)
          .pipe(takeUntil(this.subSubject))
          .subscribe(
            (data) => {
              this.showMarkerOnMap(data.lat, data.lng, result.name);
            }
          );
      }
    });
    this.clearSearch();
  }

  private showMarkerOnMap(lat: number, lng: number, name: string) {
    let marker = this.getData(lat, lng);
    marker.address = name;
    window['angularAddrSearchRef'].component.setupMarker(marker);
  }

  private handlePasteEvent() {
    document.getElementById('address_search_input').addEventListener('paste', (event) => {
      event.preventDefault();
      let address = event.clipboardData.getData('text');
      if (address != null && address !== '') {
        let latLng = address.split(/[, ]+/);
        let numberRegExp = /^[-+]?\d+\.\d+$/;
        if ((numberRegExp.test(latLng[0]) && numberRegExp.test(latLng[1])) || address.match(POST_CODE_REGEX)) {
          this.gSearch = address
            .split(/[, ]+/)
            .map((str) => str.substring(0, str.indexOf('.') + 7))
            .toString();
          this.onKeydown('Paste')
        } else {
          let source = [
            PlaceSource.GOOGLE_PLACES_PASTE,
            PlaceSource.POI,
            PlaceSource.GOOGLE_ADDRESS
          ];
          this.gSearch = address;
          this.placesService.getPlaces(this.gSearch, source)
            .pipe(takeUntil(this.subSubject))
            .subscribe(
              data => {
                this.searchResults = data;
                this.hideShowMore();
                this.cdRef.detectChanges();
              });
        }
      }
    });
  }

  public onKeydown($event): void {
    switch ($event.key) {
      case 'Escape':
        this.clearSearch();
        break;
      case 'ArrowDown':
        $event.preventDefault();
        this.dropDownIndex = -1;
        break;
      case 'ArrowUp':
        $event.preventDefault();
        this.dropDownIndex = -1;
        break;
      case 'Control':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'CapsLock':
      case 'Shift':
      case 'v':
      case 'V':
        break;
      default:
        const isCtrlEvent = $event.ctrlKey
          && ($event.key === 'a' || $event.key === 'A'
            || $event.key === 'c' || $event.key === 'C'
            || $event.key === 'y' || $event.key === 'Y');
        if (!isCtrlEvent) {
          this.searchSubject.next($event);
        }
        break;
    }
  }

  private handleDefault(): void {
    if (this.gSearch && this.gSearch.length > 3) {
      let sources;
      if (this.gSearch.match(this.COORDINATES)) {
        sources = [PlaceSource.NOMINATIM_COORDINATES];
        let mapPosition = TaskUtils.toMapPositions(this.gSearch);
        this.placesService.getPlaces(this.gSearch, sources)
          .pipe(takeUntil(this.subSubject))
          .subscribe(
            (data) => {
              let addressData = this.getData(mapPosition[0].lat, mapPosition[0].lng);
              addressData.address = data.length > 1 ? data[1].name : '';
              this.setupMarker(addressData);
            }
          );
      } else if (this.gSearch.match(POST_CODE_REGEX)) {
        sources = [PlaceSource.GEONAMES];
      } else {
        sources = [PlaceSource.ARCGIS_ADDRESSES];
      }
      sources.push(PlaceSource.POI);

      this.placesService.getPlaces(this.gSearch, sources)
        .pipe(takeUntil(this.subSubject))
        .subscribe(
          data => {
            this.searchResults = data;
            this.showShowMore();
            this.cdRef.detectChanges();
          }
        );
    } else {
      this.searchResults = [];
    }
  }

  private clearSearch(): void {
    this.gSearch = '';
    this.searchResults = [];
    this.dropDownIndex = -1;
    this.hideShowMore();
    this.cdRef.detectChanges();
  }

  private showMarker(data: any) {
    if (data === null) {
      this.removeMarkers();
    } else {
      this.setupMarker(this.getData(data.lat, data.lng));
    }
  }

  public insertInto(lat: any, lng: any, index: number, uuid: any, externalPoiId: string): void {
    this.taskService.insertion({
        lat: lat,
        lng: lng,
        index: index
      }
    );
    this.hideMarker(uuid);
  }

  private hideShowMore() {
    document.getElementById('show-more').classList.add('hidden');
  }

  private showShowMore() {
    setTimeout(() => document.getElementById('show-more').classList.remove('hidden'), 500);
  }

  private copyToClipboard(lat: any, lng: any) {
    this.notification.copiedSuccessfully();
    this.clipBoardService.copyFromContent(lat, lng);
  }

  public recenterMapToPoint(uuid: string) {
    let addressData = this.datasMap.get(uuid);
    let infoWindow = this.infosMap.get(uuid);
    let marker = this.markersMap.get(uuid);
    let zoom = this.map.getZoom() > ZOOM.DEFAULT ? this.map.getZoom() : ZOOM.DEFAULT;
    this.map.setCenter(addressData.location);
    this.map.setZoom(zoom);
    infoWindow.open(this.map, marker);
  }

  public getMarkerLocation(): string {
    let addressData = this.datasMap.get(this.lastMarkerPosition);
    let lat = addressData.location.lat().toFixed(6);
    let lng = addressData.location.lng().toFixed(6);
    return addressData.address != null
      ? addressData.address + ', ' + lat + ', ' + lng
      : lat + ', ' + lng;
  }

  public clearLastPositionMarker() {
    this.recenterLastMarker = false;
    this.lastMarkerPosition = null;
  }

  private handleDebounce($event): void {
    this.handleDefault();
  }

  public ngOnDestroy(): void {
    window['angularAddrSearchRef'] = null;
    if (this.map.controls[google.maps.ControlPosition.LEFT_TOP] != null) {
      this.map.controls[google.maps.ControlPosition.LEFT_TOP].clear();
    }
    this.removeMarkers();
    this.removeMouseEvents();
    if (this.searchSubject != null) {
      this.searchSubject.unsubscribe();
    }
    SubscriptionUtils.safeUnsubscribe(this.markerSubscription);
    SubscriptionUtils.safeUnsubscribeSubject(this.subSubject);
  }
}
